import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  fetchBatches,
  fetchProjects,
  finalizeBatch,
} from '../../../services/batchService';

const initialFilters = {
  project: '',
  status: '',
  search: '',
  dateFrom: '',
  dateTo: '',
};

const initialPendingFilters = {
  project: '',
  status: '',
  dateFrom: '',
  dateTo: '',
};

export function useBatches(initialBatchId = null) {
  const [projects, setProjects] = useState([]);
  const [batches, setBatches] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [pendingFilters, setPendingFilters] = useState(initialPendingFilters);
  const [showVoid, setShowVoid] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState(initialBatchId);
  const [loading, setLoading] = useState(false);
  const initialBatchSelectionApplied = useRef(false);

  useEffect(() => {
    fetchProjects().then(setProjects).catch(console.error);
  }, []);

  useEffect(() => {
    initialBatchSelectionApplied.current = false;
  }, [initialBatchId]);

  const loadBatches = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchBatches({
        project: filters.project || undefined,
        status: filters.status || undefined,
        search: filters.search || undefined,
      });
      setBatches(data);
      if (
        selectedBatchId &&
        data.every((batch) => batch.id !== selectedBatchId)
      ) {
        setSelectedBatchId(null);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filters.project, filters.search, filters.status, selectedBatchId]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  useEffect(() => {
    if (
      initialBatchId &&
      !initialBatchSelectionApplied.current &&
      batches.some((batch) => batch.id === initialBatchId)
    ) {
      setSelectedBatchId(initialBatchId);
      initialBatchSelectionApplied.current = true;
    }
  }, [batches, initialBatchId]);

  const visibleBatches = useMemo(
    () => batches.filter((batch) => showVoid || batch.status !== 'void'),
    [batches, showVoid]
  );

  const handleQuickFinalize = useCallback(
    async (id, event) => {
      event?.stopPropagation?.();
      await finalizeBatch(id);
      await loadBatches();
    },
    [loadBatches]
  );

  const syncPendingFilters = useCallback(() => {
    setPendingFilters({
      project: filters.project || '',
      status: filters.status || '',
      dateFrom: filters.dateFrom || '',
      dateTo: filters.dateTo || '',
    });
  }, [filters.dateFrom, filters.dateTo, filters.project, filters.status]);

  const applyPendingFilters = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      project: pendingFilters.project,
      status: pendingFilters.status,
      dateFrom: pendingFilters.dateFrom,
      dateTo: pendingFilters.dateTo,
    }));
  }, [pendingFilters.dateFrom, pendingFilters.dateTo, pendingFilters.project, pendingFilters.status]);

  const clearFilters = useCallback(() => {
    setPendingFilters(initialPendingFilters);
    setFilters((prev) => ({
      ...prev,
      project: '',
      status: '',
      dateFrom: '',
      dateTo: '',
    }));
  }, []);

  const updatePendingFilter = useCallback((field, value) => {
    setPendingFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const togglePendingStatus = useCallback((value) => {
    setPendingFilters((prev) => ({
      ...prev,
      status: prev.status === value ? '' : value,
    }));
  }, []);

  const updateSearch = useCallback((value) => {
    setFilters((prev) => ({ ...prev, search: value }));
  }, []);

  const selectBatch = useCallback((id) => {
    setSelectedBatchId(id);
  }, []);

  return {
    projects,
    batches,
    visibleBatches,
    filters,
    pendingFilters,
    showVoid,
    selectedBatchId,
    loading,
    setShowVoid,
    syncPendingFilters,
    applyPendingFilters,
    clearFilters,
    updatePendingFilter,
    togglePendingStatus,
    updateSearch,
    selectBatch,
    handleQuickFinalize,
    loadBatches,
  };
}
